export type DisclosureSpec = {
	/** Escape returns focus to the trigger only from within this scope. */
	scope: HTMLElement;
	/** Where focus lives while open. Leaving it dismisses. */
	contains: HTMLElement;
	isOpen: () => boolean;
	close: (returnFocus: boolean) => void;
	signal: AbortSignal;
	retainsFocus?: (target: EventTarget | null) => boolean;
};

/** Dismissal for a disclosure: focus leaves, or Escape. A dialog shares neither — the drawer
    contains focus rather than being dismissed by it, and its Escape unwinds a level first. */
export const createDisclosure = ({
	scope,
	contains,
	isOpen,
	close,
	signal,
	retainsFocus,
}: DisclosureSpec): void => {
	const inside = (node: EventTarget | null) =>
		(node instanceof Element && contains.contains(node)) || retainsFocus?.(node) === true;

	contains.addEventListener(
		"focusout",
		(event) => {
			/* `null` is the window losing focus, never a Tab. */
			if (!isOpen() || event.relatedTarget === null || inside(event.relatedTarget)) return;

			close(false);
		},
		{ signal },
	);

	/* On the document, not the panel: the trigger sits outside it, so a press can land anywhere. */
	document.addEventListener(
		"keydown",
		(event) => {
			if (event.key !== "Escape" || !isOpen()) return;

			close(scope.contains(document.activeElement));
		},
		{ signal },
	);
};
